/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Box from '@mui/material/Box';

import {
  getEmailAddress,
  getPhoneNumber,
} from '../../utils/session/selectors';
import useComponentEntry from '../../hooks/useComponentEntry';
import CancelChildSessionWrapper from '../../shared/CancelChildSessionWrapper/CancelChildSessionWrapper';
import PaymentMethodChannelSelector from '../components/PaymentMethodChannelSelector/PaymentMethodChannelSelector';
import AwaitingUserResponseContainer from '../components/AwaitingUserResponse/AwaitingUserResponseContainer';
import UnifiedPaymentFormEntry from '../UnifiedPaymentForm/UnifiedPaymentFormEntry';
import paymentMethodEntryViewState from '../../utils/capabilities/paymentMethodEntryViewState';
import { getBackButtonHandler } from '../../utils/capabilities/back-button/getBackButtonHandler';
import getBackButtonDetails from '../../utils/capabilities/back-button/getBackButtonDetails';
import { NotificationContext } from '../contextStore/NotificationContext';
import { getConfiguredChannelsForAgent } from '../../utils/session/getConfiguredChannelsForAgent';
import usePaymentMethodEntryLoader from '../../shared/hooks/payment-method-entry/usePaymentMethodEntryLoader';
import useEnvironmentContextProvider from '../contextStore/EnvironmentContext/useEnvironmentContextProvider';
import SycurioPhoneEntrySubmitContainer from '../sycurio-pci-forms/SycurioPhoneEntrySubmitContainer/SycurioPhoneEntrySubmitContainer';
import SycurioTelephonicEntry from '../sycurio-pci-forms/sycurio-telephonic-entry/SycurioTelephonicEntry';
import { TELEPHONIC_ENTRY_ERROR_MESSAGES } from '../sycurio-pci-forms/constants/messages';
import usePciSessionContext from '../../hooks/usePciSessionContext/usePciSessionContext';

import type { AddPaymentMethodBaseProps } from './types/AddPaymentMethodBaseProps';
import AddPaymentMethodContainer from './AddPaymentMethodContainer';
import NotificationsMethodsContainer from './components/NotificationMethods/NotificationsMethodsContainer';
import AddPaymentMethodAchContainer from './components/AddPaymentMethodAchContainer/AddPaymentMethodAchContainer';
import AddPaymentMethodEntryErrorHandler from './components/AddPaymentMethodEntryErrorHandler/AddPaymentMethodEntryErrorHandler';

const AddPaymentMethodEntry = ({
  customerApi,
  customerId,
  onCancel,
  onSuccess,
  stripeApi,
  vendorPlatformKey,
  onBackClick,
  onBeforeSubmit,
  onError,
}: AddPaymentMethodBaseProps) => {
  const {
    localData,
    originalCheckoutSessionResponse,
    setLocalData,
    setShouldCancelChildSession,
    setIsChildStatusCanceled,
    isChildStatusCanceled,
    shouldCancelChildSession,
    errorType,
    setErrorType,
    isAgentAssistedSession,
    isInitializing,
    setApplicationData,
    checkoutSessionId,
  } = useComponentEntry();
  const telephonicEntryFailedAttempts = useRef<number>(0);

  const [pciActive, setPciActive] = useState(false);

  const { isPciActivating } = usePciSessionContext({
    pciActive,
  });

  const { closeNotification, notify } = useContext(
    NotificationContext,
  );

  const { containerConfig } = useEnvironmentContextProvider();

  const viewState = paymentMethodEntryViewState({
    errorType,
    componentEntryLocalData: localData,
    checkoutSessionDetails: originalCheckoutSessionResponse,
  });

  useEffect(() => {
    if (viewState !== 'unified_payment_method_form') {
      setPciActive(false);
    }
  }, [viewState]);

  useEffect(() => {
    return () => {
      setPciActive(false);
    };
  }, []);

  const paymentMethodChannels =
    getConfiguredChannelsForAgent(
      originalCheckoutSessionResponse,
    ) ?? [];

  const backButtonHandler = getBackButtonHandler({
    backButtonDetails: getBackButtonDetails({
      errorType,
      componentEntryLocalData: localData,
      sessionDetails: originalCheckoutSessionResponse,
      containerConfig,
    }),
    closeNotification,
    componentEntryLocalData: localData,
    onBackButtonParentHandler: onBackClick,
    setComponentEntryLocalData: setLocalData,
    clearError: () => {
      setErrorType(undefined);
    },

    isSycurioPhoneEntryForm:
      viewState === 'sycurio_telephonic_entry',
  });

  const {
    isLoading: isPaymentMethodLoading,
    onAchFormReady,
    onCardFormReady,
    onManagedComponentReady,
    reset,
  } = usePaymentMethodEntryLoader(viewState, isInitializing);

  const isLoading = isPaymentMethodLoading || isPciActivating;

  const handleCardFormReady = useCallback(() => {
    setPciActive(true);
    onCardFormReady();
  }, [onCardFormReady]);

  const handleAchFormReady = useCallback(() => {
    setPciActive(true);
    onAchFormReady();
  }, [onAchFormReady]);

  const renderAddPaymentMethodCardContainer = ({
    formTitle,
    onBackClickLocal,
    selectedPaymentMethod,
  }: {
    formTitle: string;
    onBackClickLocal?: () => void;
    selectedPaymentMethod: string;
  }) => (
    <AddPaymentMethodContainer
      customerApi={customerApi}
      vendorPlatformKey={vendorPlatformKey}
      formTitle={formTitle}
      stripeApi={stripeApi}
      customerId={customerId}
      onSuccess={onSuccess}
      onBeforeSubmit={onBeforeSubmit}
      onBackClick={onBackClickLocal}
      onCancel={onCancel}
      onLoadComplete={handleCardFormReady}
      onError={onError}
      isInFocus={selectedPaymentMethod === 'CARD'}
    />
  );

  const renderAddPaymentMethodAchContainer = ({
    formTitle,
    onBackClickLocal,
    selectedPaymentMethod,
  }: {
    formTitle: string;
    onBackClickLocal?: () => void;
    selectedPaymentMethod: string;
  }) => (
    <AddPaymentMethodAchContainer
      customerId={customerId}
      onCancel={onCancel}
      customerApi={customerApi}
      onSuccess={onSuccess}
      formTitle={formTitle}
      onBackClick={onBackClickLocal}
      onBeforeSubmit={onBeforeSubmit}
      onLoadComplete={handleAchFormReady}
      onError={onError}
      isInFocus={selectedPaymentMethod === 'BANK_ACCOUNT'}
    />
  );

  const handleErrorNotificationsMethods = useCallback(() => {
    setErrorType('error');
  }, [setErrorType]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    reset();
  }, [viewState, isInitializing, reset]);

  if (isInitializing) {
    return null;
  }

  switch (viewState) {
    case 'cancel_child_session':
      return (
        <CancelChildSessionWrapper
          errorType={errorType!}
          isChildStatusCanceled={isChildStatusCanceled}
          onCancel={onCancel}
          originalCheckoutSessionResponse={
            originalCheckoutSessionResponse
          }
          setErrorType={setErrorType}
          setIsChildStatusCanceled={setIsChildStatusCanceled}
          setShouldCancelChildSession={
            setShouldCancelChildSession
          }
          shouldCancelChildSession={shouldCancelChildSession}
          setLocalData={setLocalData}
          childSessionId={localData.childSessionId}
          selectedChannelType={localData.selectedChannelType}
        />
      );
    case 'channel_selector':
      return (
        <PaymentMethodChannelSelector
          userJourney="ADD_PAYMENT_METHOD"
          hasTelephonicEntryFailedAttemptsExceeded={
            telephonicEntryFailedAttempts.current === 5
          }
          paymentMethodChannels={paymentMethodChannels}
          onDone={(userSelectedChannel) => {
            closeNotification();
            setLocalData((prevState) => ({
              ...prevState,
              selectedChannelType: userSelectedChannel,
              isBackToChannelSelection: false,
            }));
          }}
          onCancel={onCancel}
          onBackClick={backButtonHandler}
          onLoadComplete={onManagedComponentReady}
        />
      );
    case 'awaiting_user_response':
      return (
        <AwaitingUserResponseContainer
          onCancel={() => {
            setShouldCancelChildSession(true);
            setErrorType('cancel');
          }}
          onError={(_errorType) => {
            setErrorType(_errorType);
          }}
          onDone={({ childSessionId, paymentMethodId }) => {
            setLocalData((prevState) => ({
              ...prevState,
              childSessionHistory:
                prevState.childSessionHistory && childSessionId
                  ? [
                      ...new Set([
                        ...prevState.childSessionHistory,
                        childSessionId,
                      ]),
                    ]
                  : [],
            }));
            onSuccess({ paymentMethodId });
          }}
          setIsChildStatusCanceled={setIsChildStatusCanceled}
          destination={localData.notificationDestination!}
          paymentMethodChannel={localData.selectedChannelType!}
          userJourney="ADD_PAYMENT_METHOD"
          onLoadComplete={onManagedComponentReady}
          childSessionHistory={
            localData.childSessionHistory ?? []
          }
        />
      );
    case 'notifications_methods': {
      const defaultDestination =
        localData.selectedChannelType === 'TEXT'
          ? getPhoneNumber(originalCheckoutSessionResponse)
          : getEmailAddress(originalCheckoutSessionResponse);

      return (
        <NotificationsMethodsContainer
          userJourney="ADD_PAYMENT_METHOD"
          checkoutSessionId={checkoutSessionId}
          paymentMethodChannel={localData.selectedChannelType!}
          destination={defaultDestination}
          onDone={({
            childSessionId,
            notificationDestination,
            notificationChannel,
          }) => {
            setLocalData((prevState) => ({
              ...prevState,
              childSessionId,
              notificationDestination,
              selectedChannelType: notificationChannel,
              isBackToChannelSelection: false,
            }));
          }}
          onError={handleErrorNotificationsMethods}
          onBackClick={backButtonHandler}
          onCancel={onCancel}
          onLoadComplete={onManagedComponentReady}
        />
      );
    }
    case 'unified_payment_method_form': {
      return (
        <Box
          style={{
            display: isLoading ? 'none' : 'block',
          }}
        >
          <UnifiedPaymentFormEntry
            userJourney="ADD_PAYMENT_METHOD"
            backButtonLabel="Back"
            onBackClick={backButtonHandler}
            isAgentAssistedSession={isAgentAssistedSession}
          >
            {(selectedPaymentMethod) => {
              return (
                <>
                  <Box
                    sx={{
                      display:
                        selectedPaymentMethod === 'CARD'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    {renderAddPaymentMethodCardContainer({
                      formTitle: '',
                      selectedPaymentMethod,
                    })}
                  </Box>
                  <Box
                    sx={{
                      display:
                        selectedPaymentMethod === 'BANK_ACCOUNT'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    {renderAddPaymentMethodAchContainer({
                      formTitle: '',
                      selectedPaymentMethod,
                    })}
                  </Box>
                </>
              );
            }}
          </UnifiedPaymentFormEntry>
        </Box>
      );
    }
    case 'sycurio_telephonic_entry': {
      return (
        <SycurioTelephonicEntry
          userJourney="ADD_PAYMENT_METHOD"
          backButtonLabel="Back"
          onBackClick={backButtonHandler}
          onCancel={onCancel}
          onDone={({
            sycurioPCIResponse: {
              token,
              ...restOfSycurioPCIResponse
            },
          }) => {
            setLocalData((prevState) => ({
              ...prevState,
              paymentMethodId: token,
              telephonicEntry: {
                hasCapturedSycurioPhoneEntry: true,
                ...restOfSycurioPCIResponse,
              },
            }));
          }}
          onError={({ sycurioErrorType, message }) => {
            const sycurioErrorPropsReset = {
              selectedChannelType: undefined,
              paymentMethodId: undefined,
              telephonicEntry: undefined,
            };
            telephonicEntryFailedAttempts.current += 1;
            if (sycurioErrorType === 'sycurioIframeError') {
              setLocalData((prevState) => ({
                ...prevState,
                ...sycurioErrorPropsReset,
                isBackToChannelSelection: false,
              }));
              setErrorType('sycurioError');
            } else if (sycurioErrorType === 'expiryError') {
              setLocalData((prevState) => ({
                ...prevState,
                ...sycurioErrorPropsReset,
                isBackToChannelSelection: false,
              }));
              notify({
                severity: 'error',
                title: message,
              });
            } else {
              const exceededAttemptMessageToNotify =
                TELEPHONIC_ENTRY_ERROR_MESSAGES.telephonicEntryMaxAttemptsReached;
              setLocalData((prevState) => ({
                ...prevState,
                ...sycurioErrorPropsReset,
                isBackToChannelSelection: true,
              }));
              notify({
                severity: 'error',
                title:
                  telephonicEntryFailedAttempts.current === 5
                    ? exceededAttemptMessageToNotify
                    : message,
              });
            }
          }}
          controlSpinner={({ isLoaderOn }) => {
            setApplicationData({
              overlayLoaderConfig: { show: isLoaderOn },
            });
          }}
          sessionResponse={originalCheckoutSessionResponse}
        />
      );
    }
    case 'sycurio_phone_entry_submit_page': {
      return (
        <SycurioPhoneEntrySubmitContainer
          localData={localData}
          onCancel={onCancel}
          onSuccess={onSuccess}
          customerApi={customerApi}
          customerId={customerId}
          onError={onError}
          onBeforeSubmit={onBeforeSubmit}
          onBackClick={backButtonHandler}
        />
      );
    }
    case 'view_state_error':
    default:
      return (
        <AddPaymentMethodEntryErrorHandler
          onBackClick={backButtonHandler}
        />
      );
  }
};

export default AddPaymentMethodEntry;
